import {
  LayoutDashboard, ClipboardList, ListChecks, Users, ClipboardCheck, UserSquare, FileSearch, GitMerge,
  Briefcase, UserPlus, CalendarDays, CalendarRange, CalendarRange as StaffingIcon, LayoutTemplate,
  CalendarClock, Camera, CalendarOff, Wallet, GraduationCap, BookOpen, FileWarning, Stethoscope, Gavel,
  HeartPulse, UserCheck, ShieldAlert, FileSignature, UserCog, ShieldCheck, Mail, KeySquare, Truck, Store, MapPinCheck,
  ServerCog, Gauge, Repeat,
} from "lucide-react";

// Sectioned sidebar navigation (Priority 3 redesign). Each section is only shown if at
// least one of its items is permission-visible - empty sections are hidden entirely.
export function buildSidebarSections(perm, t) {
  return [
    {
      id: "overview",
      label: t("sidebarSection.overview"),
      items: [
        { to: "/management", label: t("nav.dashboard"), icon: LayoutDashboard, testId: "nav-dashboard", end: true, show: true },
        { to: "/management/action-center", label: t("nav.actionCenter"), icon: ClipboardList, testId: "nav-action-center", show: perm.canViewManagerDigest },
        { to: "/management/data-quality", label: t("nav.dataQuality"), icon: ListChecks, testId: "nav-data-quality", show: perm.canViewDataQuality },
      ],
    },
    {
      id: "workforce",
      label: t("sidebarSection.workforce"),
      items: [
        { to: "/management/employees", label: t("nav.employees"), icon: Users, testId: "nav-employees", show: perm.canViewEmployees },
        { to: "/management/onboarding", label: t("nav.onboarding"), icon: ClipboardCheck, testId: "nav-onboarding", show: true },
        { to: "/management/applications", label: t("nav.applications"), icon: UserSquare, testId: "nav-applications", show: perm.canManageOnboarding },
        { to: "/management/document-review", label: t("documentReview.nav"), icon: FileSearch, testId: "document-review-nav", show: perm.canManageOnboarding },
        { to: "/management/workforce/migration-review", label: t("nav.migrationReview"), icon: GitMerge, testId: "nav-migration-review", show: perm.canViewMigration },
      ],
    },
    {
      id: "recruitment",
      label: t("sidebarSection.recruitment"),
      items: [
        { to: "/management/recruitment/jobs", label: t("recruitment.jobsNav"), icon: Briefcase, testId: "nav-recruitment-jobs", show: perm.canManageRecruitment },
        { to: "/management/recruitment/applicants", label: t("recruitment.applicantsNav"), icon: UserPlus, testId: "nav-recruitment-applicants", show: perm.canManageRecruitment },
        { to: "/management/recruitment/interview-slots", label: t("recruitment.interviewSlotsNav"), icon: CalendarClock, testId: "nav-recruitment-interview-slots", show: perm.canManageRecruitment },
        { to: "/management/recruitment/interviews/calendar", label: t("recruitment.interviewCalendarNav"), icon: CalendarDays, testId: "nav-recruitment-interview-calendar", show: perm.canManageInterviews },
        { to: "/management/recruitment/interview-availability", label: t("recruitment.interviewAvailabilityNav"), icon: CalendarRange, testId: "nav-recruitment-interview-availability", show: perm.canManageInterviews },
      ],
    },
    {
      id: "scheduling",
      label: t("sidebarSection.scheduling"),
      items: [
        { to: "/management/scheduling/schedules", label: "Schedules", icon: CalendarRange, testId: "nav-schedules", show: perm.canViewScheduling },
        { to: "/management/scheduling/shift-templates", label: "Shift Templates", icon: LayoutTemplate, testId: "nav-shift-templates", show: perm.canViewScheduling },
        { to: "/management/scheduling/staffing-plan", label: "Staffing Plan", icon: StaffingIcon, testId: "nav-staffing-plan", show: perm.canViewScheduling },
        { to: "/management/scheduling/shift-swaps", label: t("shiftSwap.navLabel"), icon: Repeat, testId: "nav-shift-swaps", show: perm.canApproveShiftSwaps },
        { to: "/management/workforce/availability-review", label: "Availability Review", icon: CalendarClock, testId: "nav-availability-review", show: perm.canReviewAvailability },
        { to: "/management/timekeeping/imports", label: "Timecard Imports", icon: Camera, testId: "nav-timekeeping-imports", show: perm.canManageTimekeeping },
        { to: "/management/timekeeping/monthly", label: "Monthly Timekeeping", icon: GitMerge, testId: "nav-timekeeping-monthly", show: perm.canManageTimekeeping },
        { to: "/management/timekeeping/drive", label: "Drive Ingestion", icon: CalendarClock, testId: "nav-drive-ingestion", show: perm.canManageDriveIngestion },
        { to: "/management/settings/work-calendars", label: "Work Calendars", icon: CalendarDays, testId: "nav-work-calendars", show: perm.canViewWorkCalendar },
      ],
    },
    {
      id: "payroll",
      label: t("sidebarSection.payroll"),
      items: [
        { to: "/management/payroll", label: t("nav.payroll"), icon: Wallet, testId: "nav-payroll", show: perm.canViewPayroll },
        { to: "/management/leave", label: t("nav.leaveLedger"), icon: CalendarOff, testId: "nav-leave", end: true, show: perm.canViewLeave },
        { to: "/management/leave/mandatory-use", label: t("nav.mandatoryLeaveTracking"), icon: CalendarOff, testId: "nav-leave-mandatory", show: perm.canViewLeave },
        { to: "/management/operations/supply-pickup", label: t("nav.supplyPickupAssignments"), icon: Truck, testId: "nav-supply-pickup", show: perm.canViewSupplyPickup },
      ],
    },
    {
      id: "training",
      label: t("sidebarSection.training"),
      items: [
        { to: "/management/training", label: t("nav.trainingDashboard"), icon: GraduationCap, testId: "nav-training", end: true, show: perm.canViewTraining },
        { to: "/management/training/courses", label: t("nav.trainingCourses"), icon: BookOpen, testId: "nav-training-courses", show: perm.canViewTraining },
        { to: "/management/training/assignments", label: t("nav.trainingAssignments"), icon: ClipboardList, testId: "nav-training-assignments", show: perm.canViewTraining },
        { to: "/management/training/practical-reviews", label: t("nav.trainingPracticalReviews"), icon: ClipboardCheck, testId: "nav-training-practical-reviews", show: perm.canViewTraining },
        { to: "/management/training/certifications", label: t("nav.trainingCertifications"), icon: UserCheck, testId: "nav-training-certifications", show: perm.canViewTraining },
        { to: "/management/training/sources", label: t("nav.trainingSources"), icon: FileSearch, testId: "nav-training-sources", show: perm.canReviewTrainingSource },
      ],
    },
    {
      id: "compliance",
      label: t("sidebarSection.compliance"),
      items: [
        { to: "/management/compliance/medical", label: t("nav.medicalChecks"), icon: Stethoscope, testId: "nav-medical-checks", show: perm.canManageMedicalChecks },
        { to: "/management/article36", label: t("nav.article36"), icon: Gavel, testId: "nav-article36", show: perm.canViewLaborCompliance },
        { to: "/management/compliance/health-committee", label: t("nav.healthCommittee"), icon: HeartPulse, testId: "nav-health-committee", show: perm.canViewHealthCommittee },
        { to: "/management/compliance/long-hour-interviews", label: t("nav.longHourInterviews"), icon: UserCheck, testId: "nav-long-hour-interviews", show: perm.canViewLongHourInterview },
        { to: "/management/compliance/corrective-actions", label: t("nav.correctiveActions"), icon: FileWarning, testId: "nav-corrective-actions", show: perm.canViewCorrectiveAction },
        { to: "/management/compliance", label: t("nav.compliance"), icon: ShieldAlert, testId: "nav-compliance", end: true, show: perm.canViewLaborCompliance },
        { to: "/management/policies", label: t("nav.policies"), icon: BookOpen, testId: "nav-policies", show: true },
        { to: "/management/contracts", label: t("nav.contracts"), icon: FileSignature, testId: "nav-contracts", show: true },
      ],
    },
    {
      id: "administration",
      label: t("sidebarSection.administration"),
      items: [
        { to: "/management/security/users", label: t("nav.userAccounts"), icon: UserCog, testId: "nav-user-accounts", show: perm.canManageUsers },
        { to: "/management/security/access-profiles", label: t("nav.accessProfiles"), icon: KeySquare, testId: "nav-access-profiles", show: perm.canManageUsers },
        { to: "/management/security/store-access", label: t("nav.storeAccess"), icon: Store, testId: "nav-store-access", show: perm.canManageStoreAccess },
        { to: "/management/employees/store-assignment-review", label: t("nav.storeAssignmentReview"), icon: MapPinCheck, testId: "nav-store-assignment-review", show: perm.canReviewStoreAssignments },
        { to: "/management/email/deliveries", label: t("nav.emailDelivery"), icon: Mail, testId: "nav-email-delivery", show: perm.canManageEmailDelivery },
        { to: "/management/audit", label: t("nav.audit"), icon: ShieldCheck, testId: "nav-audit", show: perm.canReadAudit },
        { to: "/management/system/jobs", label: t("nav.systemJobs"), icon: ServerCog, testId: "nav-system-jobs", show: perm.canViewSystemJobs },
        { to: "/management/system/production-readiness", label: t("nav.productionReadiness"), icon: Gauge, testId: "nav-production-readiness", show: perm.canViewProductionReadiness },
      ],
    },
  ].map((section) => ({ ...section, items: section.items.filter((i) => i.show) }))
    .filter((section) => section.items.length > 0);
}

export function findActiveSectionId(sections, pathname) {
  for (const section of sections) {
    if (section.items.some((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to)))) {
      return section.id;
    }
  }
  return null;
}
